import { useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';

interface AuthUser {
  clerkUserId: string;
  email: string;
  userId: string;
  orgId: string;
  roles: string[];
}

/**
 * Hook for authentication using Clerk
 * Provides user info and logout functionality
 */
export function useAuth() {
  const { user, isLoaded, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  // Extract roles and org from Clerk public metadata
  const metadata = (user?.publicMetadata || {}) as {
    dbUserId?: string;
    orgId?: string;
    roles?: string[];
  };

  const authUser: AuthUser | null = isSignedIn && user
    ? {
        clerkUserId: user.id,
        email: user.emailAddresses[0]?.emailAddress || '',
        userId: metadata.dbUserId || user.id,
        orgId: metadata.orgId || '',
        roles: metadata.roles || [],
      }
    : null;

  const logout = async () => {
    // Use redirectUrl to ensure sign-out redirects within the base path
    const basePath = import.meta.env.VITE_BASE_PATH?.replace(/\/$/, '') || '';
    await signOut({ redirectUrl: `${basePath}/sign-in` });
  };

  return {
    user: authUser,
    isLoading: !isLoaded,
    isSignedIn: !!isSignedIn,
    logout,
  };
}

/**
 * Hook to check if current user has a specific role.
 * Reads from Convex (live, reactive) so role changes like director access
 * granted mid-session are reflected immediately without a sign-out.
 */
export function useHasRole(role: string) {
  const me = useQuery(api.me.get);
  return (me?.roles ?? [] as string[]).includes(role);
}

/**
 * Hook to check if current user has any of the specified roles.
 */
export function useHasAnyRole(...roles: string[]) {
  const me = useQuery(api.me.get);
  const userRoles: string[] = me?.roles ?? [];
  return roles.some((role) => userRoles.includes(role));
}
